import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import {
  CalendarReportPayload,
  ChartType,
  Column,
  CreateDatatablePayload,
  Datatable,
  CalendarData,
  IUpdateDefaultReport,
  MapReportPayload,
  Report,
  ReportDetails,
  ReportWFilter,
  RequestDatatable,
  Summary,
  UpdateDatatablePayload,
  MapData,
  CopyReportPayload,
  CopyReportResponse,
  CreateGanttPayload,
  UpdateGanttPayload,
  GanttData,
  FlexBoundaryCheckResponse,
  FlexBoundaryCheck,
  FlexDataRequest,
} from '../_models/report.models';
import { Field } from '../_models/field.model';
import { RequestAllDataDatatable, RequestCompanyDatatable } from '../_models/company.model';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private baseURL = `${environment.apiUrl}/api`;
  private mlBaseUrl = `${environment.mlBaseUrl}`;
  private FILE_VIEW = '/file';
  private TEMPLATE_FILE_VIEW = '/templatefile';
  private midURL = '/report';
  private midUserURL = '/user';
  private midApplicationURL = '/application';
  private REPORT_BY_TABLE = '/getreportbytableid?id=';
  private TABLE_FIELDS = '/gettablefields?id=';
  private POST_DATATABLE = '/postdatatable';
  private REPORT_W_FILTERS = '/getreportwithfilters?id=';
  private GET_DATATABLE = '/getdatatabledata';
  private FLEXDATA = '/getflexdata?tableId=';
  private FLEXDATA_REQUEST = '/getflexdata';
  private GET_SUMMARY = '/getreportbyid?id=';
  private FLEX_CHART_TYPES = '/getflexcharttypes';
  private EXPORT = '/export?reportId=';
  private GET_DETAILS_BY_ID = '/getdetailsbyid?id=';
  private COLUMNS = '/getreportcolumns?id=';
  private UPDATE_DEFAULT_REPORT = '/updatedefaultreport';
  private CALENDAR = '/calendar';
  private MAP = '/map';
  private GANTT = '/gantt';
  private DATA = '/data?reportId=';
  private COPY = '/copy';
  private GET_COMPANIES_DATATABLE_URL = '/company/getdatatable';
  private GET_CLIENTS_DATATABLE_URL = '/clients/getall';
  private APPLICATION_USERS_URL = '/getapplicationusers?applicationId=';
  private APPLICATION_USERS_IP_TRACKING_URL = '/getiptrackingdatatable';
  private GET_FLEX_BOUNDARY_CHECK = '/report/flexboundarycheck';
  private GET_DEPENDENCY_LINKS = '/report/gantt/adddependencylinks';
  private SAVE_GANTT_BASELINES = '/report/gantt/addbaselines';
  private GET_BULK_UPDATE_LINKS = '/report/bulk';
  private TRAIN = '/train';
  private RESULTS = '/results';
  private PREDICT = '/predict';
  private COMP_VISION_TRAIN = '/compvisionTrain';
  private COMP_VISION_PREDICT = '/compvisionPredict';
  private CHAT = '/Chat';
  private ClOSE_CHAT = '/CloseChatSession';
  private CHAT_PDF = '/ChatPDF';
  private ClOSE_CHAT_PDF = '/CloseChatPDFSession';
  private FUZZY_MATCH = '/FuzzyMatch';
  public GET_COMPANY_EXPORT_URL = '/company/export';
  public GET_CLIENTS_EXPORT_URL = '/clients/export';
  public GET_USERS_EXPORT_URL = '/user/export';
  public GET_IPTRACKING_EXPORT_URL = '/application/iptracking/export';

  public get getApplicationUserUrl() {
    return `${this.baseURL}${this.midUserURL}${this.APPLICATION_USERS_URL}`;
  }

  public get getApplicationUserIpTrackingUrl() {
    return `${this.baseURL}${this.midApplicationURL}${this.APPLICATION_USERS_IP_TRACKING_URL}`;
  }

  public get getCompaniesDatatableUrl() {
    return `${this.baseURL}${this.GET_COMPANIES_DATATABLE_URL}`;
  }

  public get getClientsDatatableUrl() {
    return `${this.baseURL}${this.GET_CLIENTS_DATATABLE_URL}`;
  }

  public get getReportByTable() {
    return `${this.baseURL}${this.midURL}${this.REPORT_BY_TABLE}`;
  }

  public get getReportFields() {
    return `${this.baseURL}${this.midURL}${this.TABLE_FIELDS}`;
  }

  public get getCreateDatatableUrl() {
    return `${this.baseURL}${this.midURL}${this.POST_DATATABLE}`;
  }

  public get getReportWFilterUrl() {
    return `${this.baseURL}${this.midURL}${this.REPORT_W_FILTERS}`;
  }

  public get getReportUrl() {
    return `${this.baseURL}${this.midURL}`;
  }

  public get getDatatableUrl() {
    return `${this.baseURL}${this.midURL}${this.GET_DATATABLE}`;
  }

  public get getFlexdataUrl() {
    return `${this.baseURL}${this.midURL}${this.FLEXDATA}`;
  }

  public get getFlexdataRequestUrl() {
    return `${this.baseURL}${this.midURL}${this.FLEXDATA_REQUEST}`;
  }

  public get getSummaryUrl() {
    return `${this.baseURL}${this.midURL}${this.GET_SUMMARY}`;
  }

  public get getFlexChartTypeUrl() {
    return `${this.baseURL}${this.midURL}${this.FLEX_CHART_TYPES}`;
  }

  public get getExportUrl() {
    return `${this.baseURL}${this.midURL}${this.EXPORT}`;
  }

  public get getDetailsByIdUrl() {
    return `${this.baseURL}${this.midURL}${this.GET_DETAILS_BY_ID}`;
  }

  public get getReportColumnsUrl() {
    return `${this.baseURL}${this.midURL}${this.COLUMNS}`;
  }

  public get getUpdateDefaultReport() {
    return `${this.baseURL}${this.midURL}s${this.UPDATE_DEFAULT_REPORT}`;
  }

  public get getCalendarUrl() {
    return `${this.baseURL}${this.midURL}${this.CALENDAR}`;
  }

  public get getMapUrl() {
    return `${this.baseURL}${this.midURL}${this.MAP}`;
  }

  public get getGanttUrl() {
    return `${this.baseURL}${this.midURL}${this.GANTT}`;
  }

  public get getCalendarDataUrl() {
    return `${this.baseURL}${this.midURL}${this.CALENDAR}${this.DATA}`;
  }

  public get getMapDataUrl() {
    return `${this.baseURL}${this.midURL}${this.MAP}/data`;
  }

  public get getGanttDataUrl() {
    return `${this.baseURL}${this.midURL}${this.GANTT}${this.DATA}`;
  }

  public get getCopyReportUrl() {
    return `${this.baseURL}${this.midURL}${this.COPY}`;
  }

  public get getFileViewUrl() {
    return `${this.baseURL}${this.FILE_VIEW}`;
  }

  public get getTemplateFileViewUrl() {
    return `${this.baseURL}${this.TEMPLATE_FILE_VIEW}`;
  }

  public get getFlexBoundaryCheckUrl() {
    return `${this.baseURL}${this.GET_FLEX_BOUNDARY_CHECK}`;
  }

  public get getMlTrainUrl() {
    return `${this.mlBaseUrl}${this.TRAIN}`;
  }

  public get getTrainedMlModelResultUrl() {
    return `${this.mlBaseUrl}${this.RESULTS}`;
  }

  public get predictMlTrainedModelUrl() {
    return `${this.mlBaseUrl}${this.PREDICT}`;
  }

  public get getCompVisionTrainUrl() {
    return `${this.mlBaseUrl}${this.COMP_VISION_TRAIN}`;
  }

  public get getCompVisionPredictUrl() {
    return `${this.mlBaseUrl}${this.COMP_VISION_PREDICT}`;
  }

  public get getChatBotUrl(){
    return `${this.mlBaseUrl}${this.CHAT}`;
  }

  public get getCloseChatBot(){
    return `${this.mlBaseUrl}${this.ClOSE_CHAT}`;
  }
  public get getChatPdfUrl(){
    return `${this.mlBaseUrl}${this.CHAT_PDF}`;
  }

  public get getDependencyLinkUrl(){
    return `${this.baseURL}${this.GET_DEPENDENCY_LINKS}`;
  }
  public get getSaveGanttBaselinesUrl() {
    return `${this.baseURL}${this.SAVE_GANTT_BASELINES}`;
  }
  public get getBulkUpdateLinkUrl(){
    return `${this.baseURL}${this.GET_BULK_UPDATE_LINKS}`;
  }
  public get getCloseChatPdfBot(){
    return `${this.mlBaseUrl}${this.ClOSE_CHAT_PDF}`;
  }
  public get getFuzzyMatchResponse(){
    return `${this.mlBaseUrl}${this.FUZZY_MATCH}`;
  }

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * get file
   */
  getFile = (
    tableId: any,
    recordId: number,
    fieldId: number,
    fromUi: boolean = false
  ): Observable<any> =>
    this.http.get<any>(
      `${this.getFileViewUrl}?t=${tableId}&a=${recordId}&f=${fieldId}&ui=${fromUi}`,
      {}
    );

  /**
   * get template file
   */
  getTemplateFile = (
    templateId: any,
    fromUi: boolean = false
  ): Observable<any> =>
    this.http.get<any>(
      `${this.getTemplateFileViewUrl}?templateId=${templateId}&fromUi=${fromUi}`,
      {}
    );

  /**
   * Get reports
   */
  getReports = (
    tableId: string,
    inForms: boolean,
    formId: string | null,
    formType: string,
    fromTableSettings: boolean
  ): Observable<Array<Report>> =>
    this.http.get<Array<Report>>(
      `${this.getReportByTable}${tableId}&inForms=${inForms}${
        formId ? `&formId=${formId}` : ''
      }&formType=${formType}&fromTableSettings=${fromTableSettings}`,
      {}
    );

  /**
   * Get report table fields
   */
  getReportTableFields = (tableId: string): Observable<Array<Field>> =>
    this.http.get<Array<Field>>(`${this.getReportFields}${tableId}`, {});

  /**
   * create report
   */
  createDatatableReport = (
    payload: CreateDatatablePayload
  ): Observable<Report> =>
    this.http.post<Report>(`${this.getCreateDatatableUrl}`, payload, {});

  /**
   * Get report with filters
   */
  getReportWFilters = (tableId: string,fromDashboardSummary: boolean = false): Observable<ReportWFilter> =>
    this.http.get<ReportWFilter>(`${this.getReportWFilterUrl}${tableId}&fromDashboardSummary=${fromDashboardSummary}`, {});

  /**
   * update report
   */
  updateDatatableReport = (
    payload: UpdateDatatablePayload
  ): Observable<Report> =>
    this.http.put<Report>(`${this.getCreateDatatableUrl}`, payload, {});

  /**
   * delete report
   */
  deleteReport = (reportId: string): Observable<void> =>
    this.http.delete<void>(`${this.getReportUrl}?Id=${reportId}`, {});

  /**
   * Get datatable
   */
  getDatatable = (payload: RequestDatatable): Observable<Datatable> =>
    this.http.post<Datatable>(`${this.getDatatableUrl}`, payload, {});

  /**
   * Get flexdata
   */
  getFlexdata = (
    tableId: string,
    reportId: string,
    create: boolean,
    edit: boolean,
    fromDashboard: boolean
  ): Observable<Array<Array<any>>> =>
    this.http.get<Array<Array<any>>>(
      `${this.getFlexdataUrl}${tableId}${
        reportId ? `&reportId=${reportId}` : ``
      }${create ? '&create=true' : ''}${edit ? '&edit=true' : ''}${
        fromDashboard ? '&fromDashboard=true' : ''
      }`,
      {}
    );

  /**
   * Get flexdata
   */
  getFlexDataRequest = (
    payload: FlexDataRequest
  ): Observable<Array<Array<any>>> =>
    this.http.post<Array<Array<any>>>(
      `${this.getFlexdataRequestUrl}`,
      payload,
      {}
    );

  /**
   * Get summary report
   */
  getSummaryReport = (
    tableId: string,
    reportId: string,
    fromDashboard: boolean
  ): Observable<Summary> =>
    this.http.get<Summary>(
      `${this.getSummaryUrl}${reportId}&tableId=${tableId}&fromDashboard=${fromDashboard}`,
      {}
    );

  /**
   * Get chart types
   */
  getFlexChartTypes = (): Observable<Array<ChartType>> =>
    this.http.get<Array<ChartType>>(`${this.getFlexChartTypeUrl}`, {});

  /**
   * Export
   */
  exportReport = (
    tableId: string,
    reportId: any,
    exportType: string,
    size: number = -1,
    skip: number = 0,
    lookupFieldValue: any,
    referenceFieldId: string = '-1',
    isEmbeddedReport: boolean = false,
    fromAuditLog: boolean = false,
    fromNotificationAuditLog: boolean = false
  ): Observable<Blob> =>
    this.http.get<Blob>(
      `${this.getExportUrl}${reportId}&tableId=${tableId}&exportType=${exportType}&size=${size}&skip=${skip}&lookupFieldValue=${lookupFieldValue}&referenceFieldId=${referenceFieldId}&isEmbeddedReport=${isEmbeddedReport}`,
      {
        observe: 'body',
        // responseType: 'blob' as 'json',
      }
    );

  /**
   * get all companies
   */
  getAllCompaniesDatatable = (
    payload: RequestCompanyDatatable
  ): Observable<Datatable> =>
    this.http.post<Datatable>(`${this.getCompaniesDatatableUrl}`, payload);

  /**
   * get all data csv/pdf
   */
  getAllData = (
    requestUrl:string,
    payload: RequestAllDataDatatable
  ): Observable<Blob> =>
    this.http.post<Blob>(`${this.baseURL}${requestUrl}`, payload);

  /**
   * get all clients
   */
  getAllClientsDatatable = (
    payload: RequestCompanyDatatable
  ): Observable<Datatable> =>
    this.http.post<Datatable>(`${this.getClientsDatatableUrl}`, payload);

  getApplicationUsers = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.getApplicationUserUrl}${applicationId}`,
      {}
    );

  getApplicationUsersIpTracking = (
    payload: RequestCompanyDatatable
  ): Observable<Datatable> =>
    this.http.post<Datatable>(
      `${this.getApplicationUserIpTrackingUrl}`,
      payload
    );

  /**
   * Get report detals
   */
  getDetailsById = (
    tableId: string,
    reportId: string,
    id: number,
    summaryfieldId: number | null,
    formKey:string | null = null,
    fromProcess:boolean = false,
    fromRecordEdit:boolean = false,
  ): Observable<ReportDetails> =>
    this.http.get<ReportDetails>(
      `${this.getDetailsByIdUrl}${id}&tableId=${tableId}&reportId=${reportId}${
        summaryfieldId ? `&summaryfieldId=${summaryfieldId}` : ''
      }&formId=${formKey}&fromProcess=${fromProcess}&fromRecordEdit=${fromRecordEdit}`
    );

  /**
   * Get report columns
   */
  getReportColumns = (
    tableId: string,
    id: string,
    fromDashboard: boolean
  ): Observable<Array<Column>> =>
    this.http.get<Array<Column>>(
      `${this.getReportColumnsUrl}${id}&tableId=${tableId}&fromDashboard=${fromDashboard}`
    );

  /**
   * update default report
   */
  updateDefaultReport = (payload: IUpdateDefaultReport): Observable<void> =>
    this.http.put<void>(`${this.getUpdateDefaultReport}`, payload);

  /**
   * create calendar report
   */
  createCalendarReport = (payload: CalendarReportPayload): Observable<void> =>
    this.http.post<void>(`${this.getCalendarUrl}`, payload);

  /**
   * update calendar report
   */
  updateCalendarReport = (payload: CalendarReportPayload): Observable<void> =>
    this.http.put<void>(`${this.getCalendarUrl}`, payload);

  /**
   * create map report
   */
  createMapReport = (payload: MapReportPayload): Observable<void> =>
    this.http.post<void>(`${this.getMapUrl}`, payload);

  /**
   * update map report
   */
  updateMapReport = (payload: MapReportPayload): Observable<void> =>
    this.http.put<void>(`${this.getMapUrl}`, payload);

  /**
   * get map fields
   */
  getMapSeriesFields = (id: string): Observable<Array<Field>> =>
    this.http.get<Array<Field>>(`${this.getMapUrl}/getseriesfieldsfortable?tableId=${id}`);

  /**
   * create gantt report
   */
  createGanttReport = (payload: CreateGanttPayload): Observable<void> =>
    this.http.post<void>(`${this.getGanttUrl}`, payload);

  /**
   * update gantt report
   */
  updateGanttReport = (payload: UpdateGanttPayload): Observable<void> =>
    this.http.put<void>(`${this.getGanttUrl}`, payload);

  /**
   * Get calendar data
   */
  getCalendarData = (
    reportId: string,
    fromDashboard: boolean,
    isEmbeddedReport:boolean = false,
    referenceFieldKey:string = '',
    lookupFieldValue:string = ''
  ): Observable<CalendarData> =>
    this.http.get<CalendarData>(
      `${this.getCalendarDataUrl}${reportId}&fromDashboard=${fromDashboard}&isEmbeddedReport=${isEmbeddedReport}&referenceFieldKey=${referenceFieldKey}&lookupFieldValue=${lookupFieldValue}`
    );

  /**
   * Get map data
   */
  getMapData = (
    reportId: string,
    fromDashboard: boolean,
    conditions?:any
  ): Observable<MapData> =>
    this.http.post<MapData>(
      `${this.getMapDataUrl}`,{
        ReportId:reportId,
        FromDashboard:fromDashboard,
        Conditions:conditions || null
      }
    );

  /**
   * Get gantt data
   */
  getGanttData = (
    reportId: string,
    fromDashboard: boolean,
    isEmbeddedReport:boolean = false,
    referenceFieldKey:string = '',
    lookupFieldValue:string = ''
  ): Observable<GanttData> =>
    this.http.get<GanttData>(
      `${this.getGanttDataUrl}${reportId}&fromDashboard=${fromDashboard}&isEmbeddedReport=${isEmbeddedReport}&referenceFieldKey=${referenceFieldKey}&lookupFieldValue=${lookupFieldValue}`
    );

  /**
   * Copy Report
   */
  copyReport = (payload: CopyReportPayload): Observable<CopyReportResponse> =>
    this.http.post<CopyReportResponse>(`${this.getCopyReportUrl}`, payload);

  getFlexBoundaryCheck = (
    payload: FlexBoundaryCheck
  ): Observable<FlexBoundaryCheckResponse> =>
    this.http.post<FlexBoundaryCheckResponse>(
      `${this.getFlexBoundaryCheckUrl}`,
      payload,
      {}
    );

  createMlTrainModel = (payload: any): Observable<any> =>
    this.http.post(`${this.getMlTrainUrl}`, payload);

  getTrainedMlModelResults = (payload: any): Observable<any> =>
    this.http.post(`${this.getTrainedMlModelResultUrl}`, payload);

  predictMlTrainedModel = (payload: any): Observable<any> =>
    this.http.post(`${this.predictMlTrainedModelUrl}`, payload);

  createCompVisionModel = (payload: any): Observable<any> =>
    this.http.post(`${this.getCompVisionTrainUrl}`, payload);

  predictCompVisionModel = (payload: any): Observable<any> =>
    this.http.post(`${this.getCompVisionPredictUrl}`, payload);

  getChatResponse = (payload: any): Observable<any> =>
    this.http.post(`${this.getChatBotUrl}`, payload);

  getCloseChatResponse = (payload: any): Observable<any> =>
    this.http.post(`${this.getCloseChatBot}`, payload);

  getChatPDFResponse = (payload: any): Observable<any> =>
    this.http.post(`${this.getChatPdfUrl}`, payload);

  getClosePdfResponse = (payload: any): Observable<any> =>
    this.http.post(`${this.getCloseChatPdfBot}`, payload);

  saveDependencyLinks = (payload: any): Observable<any> =>
    this.http.post(`${this.getDependencyLinkUrl}`, payload);

  saveGanttBaselines = (payload: any): Observable<any> =>
    this.http.post(`${this.getSaveGanttBaselinesUrl}`, payload);

  bulkUpdateReports = (payload: any): Observable<any> =>
    this.http.put(`${this.getBulkUpdateLinkUrl}`, payload);

  predictFuzzyMatch = (payload: any): Observable<any> =>
    this.http.post(`${this.getFuzzyMatchResponse}`, payload);
}
